import { Controller } from "@hotwired/stimulus"

// Reloads a child select's options when its parent select changes. The parent's
// value replaces ":value" in the url value, and the JSON response can be labels,
// [label, value] pairs, or an object keyed by value whose entries are { name }.
//
// After loading, the child stays enabled even with no options, so its blank
// value is submitted and clears a stale saved value.
export default class extends Controller {
  static targets = ["child"]
  static values = { url: String }

  #initialValue = ""
  #request = null

  childTargetConnected(element) {
    this.#initialValue = element.value
    element.disabled = ![...element.options].some((option) => option.value)
  }

  disconnect() {
    this.#request?.abort()
  }

  async update({ target }) {
    this.#request?.abort()
    this.#request = null
    this.#replaceOptions([])
    this.childTarget.disabled = Boolean(target.value)

    if (!target.value) return

    const request = new AbortController()
    this.#request = request

    try {
      const url = this.urlValue.replace(":value", encodeURIComponent(target.value))
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: request.signal,
      })
      if (!response.ok) throw new Error(`Couldn't load options (${response.status})`)

      this.#replaceOptions(this.#normalize(await response.json()))
    } catch (error) {
      if (error.name !== "AbortError") console.error(error)
    } finally {
      if (this.#request === request) {
        this.#request = null
        this.childTarget.disabled = false
      }
    }
  }

  #replaceOptions(options) {
    const select = this.childTarget
    for (const option of [...select.options]) {
      if (option.value) option.remove()
    }

    for (const [label, value] of options) {
      select.add(new Option(label, value, false, value === this.#initialValue))
    }
  }

  #normalize(data) {
    if (Array.isArray(data)) {
      return data.map((option) => (Array.isArray(option) ? option.map(String) : [option, option]))
    }

    return Object.entries(data ?? {}).map(([value, option]) => [
      option?.name ?? String(option),
      value,
    ])
  }
}
